import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useFormContext } from '../contexts/FormContext';
import { useFormData } from './useFormValues';
import { Server } from '../services/Server';
import { getSoilingBlockKeys } from '../utils/soilingSensor';

const DEFAULT_SENSOR_TYPE = 'rde300';

type SensorResult = { blockKey: string; success: boolean; error?: any };

interface FetchOptions {
  clearRatios: boolean;
  logVerb: string;
  successNoun: string;
  failVerb: string;
}

interface UseSensorDataArgs {
  sectionName: string;
  projectId?: string;
}

export const useSensorData = ({ sectionName, projectId }: UseSensorDataArgs) => {
  const { setFormData, getFormDataItem, updateDataValue, clearSoilingRatioSelections } = useFormContext();
  const formData = useFormData();
  const { postSensorDataAPI } = Server();
  const [isLoadingSensorData, setIsLoadingSensorData] = useState(false);
  const [previousSensorTypes, setPreviousSensorTypes] = useState<{ [key: string]: string }>({});

  const fetchSensorBlocks = (blockKeys: string[], { clearRatios, logVerb, successNoun, failVerb }: FetchOptions) => {
    if (blockKeys.length === 0) {
      return;
    }

    setIsLoadingSensorData(true);

    const sensorDataPromises = blockKeys.map((blockKey): Promise<SensorResult> => {
      const fileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
      const sensorType = getFormDataItem(formData, `${blockKey}.type`) || DEFAULT_SENSOR_TYPE;

      return postSensorDataAPI({ file_uid: fileUid, file_type: sensorType, project_id: projectId || '' })
        .then((sensorData) => {
          setFormData((prevFormData: any) => {
            const newFormData = { ...prevFormData };
            updateDataValue(newFormData, `${blockKey}.pattern`, sensorData);
            if (clearRatios) {
              clearSoilingRatioSelections('soiling_ratio_operational', fileUid);
              clearSoilingRatioSelections('soiling_ratio_operational_ex_post', fileUid);
            }
            return newFormData;
          });
          return { blockKey, success: true };
        })
        .catch((error) => {
          console.error(`Error ${logVerb} sensor data for block ${blockKey}:`, error);
          setFormData((prevFormData: any) => {
            const newFormData = { ...prevFormData };
            updateDataValue(newFormData, `${blockKey}.pattern`, null);
            return newFormData;
          });
          return { blockKey, success: false, error };
        });
    });

    Promise.all(sensorDataPromises)
      .then((results) => {
        const successCount = results.filter((r) => r.success).length;
        const errorCount = results.filter((r) => !r.success).length;

        if (errorCount > 0) {
          toast.error(`Failed to ${failVerb} data for ${errorCount} sensor(s)`);
        } else if (successCount > 0) {
          toast.success(`${successNoun} data for ${successCount} sensor(s)`);
        }
      })
      .finally(() => {
        setIsLoadingSensorData(false);
      });
  };

  useEffect(() => {
    if (sectionName !== 'soiling_sensor' || !formData || !formData.location?.latitude || !formData.location?.longitude) {
      return;
    }

    const blocksToLoad = getSoilingBlockKeys(formData).filter((blockKey) => !!getFormDataItem(formData, `${blockKey}.file_uid`));

    fetchSensorBlocks(blocksToLoad, { clearRatios: false, logVerb: 'refreshing', successNoun: 'Loaded', failVerb: 'load' });
  }, [sectionName, formData?.location?.latitude, formData?.location?.longitude, projectId]);

  const sensorTypesSignature = JSON.stringify(
    getSoilingBlockKeys(formData).reduce(
      (acc, key) => {
        acc[key] = getFormDataItem(formData, `${key}.type`) || DEFAULT_SENSOR_TYPE;
        return acc;
      },
      {} as { [key: string]: string },
    ),
  );

  useEffect(() => {
    if (!formData || !formData.location?.latitude || !formData.location?.longitude) {
      return;
    }

    const soilingDataKeys = getSoilingBlockKeys(formData);

    const currentSensorTypes: { [key: string]: string } = {};
    soilingDataKeys.forEach((blockKey) => {
      currentSensorTypes[blockKey] = getFormDataItem(formData, `${blockKey}.type`) || DEFAULT_SENSOR_TYPE;
    });

    const blocksToRefetch = soilingDataKeys.filter((blockKey) => {
      const fileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
      const previousType = previousSensorTypes[blockKey];
      return !!fileUid && previousType && currentSensorTypes[blockKey] !== previousType;
    });

    setPreviousSensorTypes(currentSensorTypes);

    fetchSensorBlocks(blocksToRefetch, { clearRatios: true, logVerb: 'refetching', successNoun: 'Refetched', failVerb: 'refetch' });
  }, [sensorTypesSignature, formData?.location?.latitude, formData?.location?.longitude, projectId]);

  return { isLoadingSensorData };
};
